import {
  Badge,
  Box,
  Button,
  Card,
  Code,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  Activity,
  AlertTriangle,
  Clock,
  Eye,
  EyeOff,
  GitCompare,
  Layers,
  Play,
  ShieldAlert,
  Square,
  WifiOff,
} from 'lucide-react'
import { lazy, Suspense, useMemo, useRef, useState } from 'react'
import { PageHeader, SectionHeading } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import { useMqttTopicValues } from '~/hooks/useMqttTopicValues'
import { ApiError, type ScenarioPhase, scenarioApi } from '~/lib/api'
import { buildGovernedKpi, type GovernedKpiByLine, isGovernedFresh } from '~/lib/packagingKpi'
import {
  buildLineViews,
  DIVERGENT_VENDOR_TAGS,
  LINE_LABELS,
  type LineView,
  MACHINE_LABELS,
  type MachineView,
  type OutputSample,
  PACKAGING_PREFIX,
  PACKML,
  packMlDescriptor,
  performancePalette,
  rollingLinePerformance,
} from '~/lib/packagingLine'

export const Route = createFileRoute('/_app/factory/line')({ component: LivePackagingLine })

// Lazy: Three.js is a large chunk and the data below is what actually matters, so
// the scene must never delay the numbers loading.
const PackagingLine3D = lazy(() => import('~/components/packaging/PackagingLine3D'))

const PERFORMANCE_THRESHOLD = 90

/**
 * How each scenario phase reads on the banner.
 *
 * Present so a second presenter opening the page can tell at a glance whether the
 * environment is free (PRD v0.3 §11.3). "Available" and "in someone's demo" must not look
 * the same, which is what a bare Healthy/Degraded pair did.
 */
const PHASE_PRESENTATION: Record<ScenarioPhase, { label: string; palette: string; hint: string }> =
  {
    baseline: {
      label: 'Baseline',
      palette: 'green',
      hint: 'Both lines at target. Free to trigger.',
    },
    onset: { label: 'Onset', palette: 'yellow', hint: 'Degradation ramping in.' },
    stalling: {
      label: 'Stalling',
      palette: 'red',
      hint: 'Fully degraded; buffers still settling.',
    },
    sustained: {
      label: 'Sustained',
      palette: 'orange',
      hint: 'Steady plateau - the phase the agent reasons over.',
    },
    recovering: { label: 'Recovering', palette: 'blue', hint: 'Cleared; ramping back to target.' },
    cooldown: {
      label: 'Cooldown',
      palette: 'purple',
      hint: 'Healthy, but a re-trigger is refused until the window closes.',
    },
  }

/** Rolling window for the displayed line rate; spans several stop cycles. */
const ROLLING_WINDOW_MS = 90_000

// What the presenter arms. Fixed rather than a form: one button is faster to drive
// live, and the cascade on the neighbours is derived server-side from this alone.
const TARGET_LINE = 'line-a'
const TARGET_MACHINE = 'label-01'
const DEGRADED_RATE = 48
const DURATION_MINUTES = 15

// The failure is armed on the case packer rather than the labeller so the two controls
// hit different machines. An agent that sees Availability drop at pack-01 while
// Performance sags at label-01 has two distinguishable stories to reason about; firing
// both at one machine would just look like a worse version of the same event.
const FAILURE_MACHINE = 'pack-01'
const FAILURE_DURATION_MINUTES = 5

function fmt(value: number | null, digits = 1, suffix = ''): string {
  return value === null ? '--' : `${value.toFixed(digits)}${suffix}`
}

/** Horizontal fill bar for a buffer level. */
function BufferBar({ label, value }: { label: string; value: number | null }) {
  // A buffer is only interesting at the extremes: a full queue means the machine
  // cannot pass product on, an empty one means nothing is arriving. Deliberately not
  // orange - that is reserved for the constraint.
  const palette = value === null ? 'gray' : value >= 90 ? 'yellow' : value <= 10 ? 'blue' : 'gray'

  return (
    <Box>
      <HStack justify="space-between" mb={1}>
        <Text fontSize="xs" color="content.secondary">
          {label}
        </Text>
        <Text fontSize="xs" fontWeight="medium">
          {fmt(value, 0, '%')}
        </Text>
      </HStack>
      <Box h="6px" bg="bg.subtle" borderRadius="full" overflow="hidden">
        <Box
          h="100%"
          w={`${Math.max(0, Math.min(100, value ?? 0))}%`}
          bg={`${palette}.solid`}
          borderRadius="full"
          transition="width 0.6s ease"
        />
      </Box>
    </Box>
  )
}

function MachineCard({ machine, lineId }: { machine: MachineView; lineId: string }) {
  const state = packMlDescriptor(machine.state)
  const halted = machine.state === PACKML.suspended || machine.state === PACKML.held
  const underTarget = machine.performance !== null && machine.performance < PERFORMANCE_THRESHOLD

  // Same rule as everywhere else: red for a stoppage, orange for the machine that
  // is still running but limiting the line.
  const borderColor = halted ? 'red.solid' : underTarget ? 'orange.solid' : 'border'

  return (
    <Card.Root borderWidth="1px" borderColor={borderColor} transition="border-color 0.3s ease">
      <Card.Body p={5}>
        <VStack align="stretch" gap={3}>
          <HStack justify="space-between" align="start">
            <VStack align="start" gap={0}>
              <Text fontWeight="semibold">{machine.label}</Text>
              <Text fontSize="xs" color="content.secondary">
                {machine.machine}
              </Text>
            </VStack>
            {/* testid: the legend reuses these words, so tests need to target
                machine badges specifically rather than matching page text. */}
            <Badge colorPalette={state.palette} variant="subtle" data-testid="machine-state">
              {state.label}
            </Badge>
          </HStack>

          <HStack justify="space-between" align="baseline">
            <Text fontSize="2xl" fontWeight="bold" lineHeight="1">
              {fmt(machine.actualRate)}
            </Text>
            <Text fontSize="xs" color="content.secondary">
              of {fmt(machine.targetRate, 0)} units/min
            </Text>
          </HStack>

          <HStack gap={2}>
            <Badge colorPalette={performancePalette(machine.performance)} variant="subtle">
              {fmt(machine.performance, 1, '%')}
            </Badge>
            {machine.microStops !== null && (
              <Text fontSize="xs" color="content.secondary">
                {machine.microStops} micro-stops
              </Text>
            )}
          </HStack>

          {/* The counts OEE is computed from. The time accumulators are published too
              but stay out of the UI - they are for Contextualize, not for reading. */}
          {machine.processed !== null && (
            <HStack justify="space-between">
              <Text fontSize="xs" color="content.secondary">
                Good / processed
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {machine.good?.toLocaleString() ?? '--'} / {machine.processed.toLocaleString()}
              </Text>
            </HStack>
          )}

          <BufferBar label="Infeed" value={machine.infeed} />
          <BufferBar label="Outfeed" value={machine.outfeed} />

          {machine.webTension !== null && (
            <HStack justify="space-between">
              <Text fontSize="xs" color="content.secondary">
                Web tension
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {fmt(machine.webTension)}
              </Text>
            </HStack>
          )}

          {lineId === 'line-b' && machine.machine === 'label-01' && (
            <Text fontSize="2xs" color="content.tertiary">
              Vendor tags: {DIVERGENT_VENDOR_TAGS.join(', ')}
            </Text>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

function LineSection({
  line,
  role,
  rolling,
}: {
  line: LineView
  role: string
  /** Rolling output rate. Null until the window is wide enough to be honest. */
  rolling: number | null
}) {
  // Prefer the rolling figure: machines now run at nameplate and stop intermittently, so
  // the instantaneous rate alternates between 100% and 0% and is unreadable.
  const shown = rolling ?? line.performance
  const palette = performancePalette(shown)
  const suspended = line.machines.filter((m) => m.state === PACKML.suspended)

  return (
    <VStack align="stretch" gap={4}>
      <Card.Root borderLeftWidth="4px" borderLeftColor={`${palette}.solid`}>
        <Card.Body p={5}>
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <VStack align="start" gap={0}>
              <HStack gap={2}>
                <Heading size="md">{line.label}</Heading>
                <Badge colorPalette="gray" variant="subtle">
                  {role}
                </Badge>
              </HStack>
              <HStack gap={2} flexWrap="wrap">
                <Text fontSize="xs" color="content.secondary">
                  Line output rate, 90% threshold
                </Text>
                {line.status.sku && (
                  <Text fontSize="xs" color="content.tertiary">
                    {line.status.sku}
                    {line.status.orderId ? ` / ${line.status.orderId}` : ''}
                    {line.status.shiftId ? ` / shift ${line.status.shiftId}` : ''}
                  </Text>
                )}
                {line.status.changeoverActive && (
                  <Badge colorPalette="blue" variant="subtle">
                    Changeover
                  </Badge>
                )}
                {line.status.plannedStopActive && (
                  <Badge colorPalette="blue" variant="subtle">
                    Planned stop
                  </Badge>
                )}
              </HStack>
            </VStack>

            <VStack align="end" gap={0}>
              <Text fontSize="3xl" fontWeight="bold" lineHeight="1" color={`${palette}.fg`}>
                {fmt(shown, 1, '%')}
              </Text>
              {suspended.length > 0 && (
                <HStack gap={1}>
                  <AlertTriangle size={12} />
                  <Text fontSize="xs" color="content.secondary">
                    {suspended.length} machine{suspended.length > 1 ? 's' : ''} suspended
                  </Text>
                </HStack>
              )}
            </VStack>
          </HStack>
        </Card.Body>
      </Card.Root>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
        {line.machines.map((m) => (
          <MachineCard key={m.machine} machine={m} lineId={line.line} />
        ))}
      </SimpleGrid>
    </VStack>
  )
}

/**
 * Side-by-side KPI comparison.
 *
 * This is the beat that rules out material and recipe causes: two lines running the
 * same SKU, one degraded and one not. Without the control group there is no way to
 * argue the problem is the machine rather than the batch.
 */
function CrossLineComparison({ lines }: { lines: LineView[] }) {
  const a = lines.find((l) => l.line === 'line-a')
  const b = lines.find((l) => l.line === 'line-b')
  const delta =
    a?.performance != null && b?.performance != null ? a.performance - b.performance : null
  const diverged = delta !== null && delta < -5

  return (
    <Card.Root bg="bg.subtle">
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          <HStack gap={3} align="start">
            <Box p={2} bg="bg.panel" borderRadius="lg">
              <GitCompare size={18} />
            </Box>
            <VStack align="start" gap={0}>
              <Text fontWeight="semibold">Same SKU, same topology, two lines</Text>
              <Text fontSize="sm" color="content.secondary">
                {diverged
                  ? 'The lines have diverged, so this is not a material or recipe issue.'
                  : 'Both lines are tracking together, so nothing line-specific is in play.'}
              </Text>
            </VStack>
          </HStack>

          <SimpleGrid columns={{ base: 1, sm: 3 }} gap={4}>
            {[a, b].map((line, i) => (
              <VStack
                key={line?.line ?? i}
                align="start"
                gap={0}
                p={3}
                bg="bg.panel"
                borderRadius="lg"
              >
                <Text fontSize="xs" color="content.secondary">
                  {line ? line.label : '--'}
                  {i === 1 ? ' (control)' : ''}
                </Text>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  lineHeight="1"
                  color={`${performancePalette(line?.performance ?? null)}.fg`}
                >
                  {fmt(line?.performance ?? null, 1, '%')}
                </Text>
              </VStack>
            ))}
            <VStack align="start" gap={0} p={3} bg="bg.panel" borderRadius="lg">
              <Text fontSize="xs" color="content.secondary">
                Difference
              </Text>
              <Text
                fontSize="2xl"
                fontWeight="bold"
                lineHeight="1"
                color={diverged ? 'red.fg' : 'content.primary'}
              >
                {delta === null ? '--' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)} pts`}
              </Text>
            </VStack>
          </SimpleGrid>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

/** Swatch used by the 3D legend, so the colours stay explained. */
function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <HStack gap={1.5}>
      <Box boxSize="10px" borderRadius="sm" bg={color} />
      <Text fontSize="xs" color="content.secondary">
        {label}
      </Text>
    </HStack>
  )
}

/**
 * The schematic 3D line, collapsible and lazily loaded.
 *
 * Collapsible because a presenter narrating numbers does not always want the scene
 * on screen, and because WebGL is a real cost on a shared laptop driving a call.
 */
function LineVisual({
  lines,
  constraintByLine,
}: {
  lines: LineView[]
  constraintByLine?: Record<string, string | undefined>
}) {
  const [show, setShow] = useState(true)

  return (
    <Card.Root overflow="hidden">
      <Card.Body p={0}>
        <HStack justify="space-between" px={5} py={3} flexWrap="wrap" gap={3}>
          <VStack align="start" gap={0}>
            <Text fontWeight="semibold">Line visual</Text>
            <Text fontSize="xs" color="content.secondary">
              Product accumulates where the line is constrained. Drag to orbit, scroll to zoom.
            </Text>
          </VStack>
          <HStack gap={4} flexWrap="wrap">
            <HStack gap={3} flexWrap="wrap">
              <Swatch color="#22c55e" label="Producing" />
              <Swatch color="#ef4444" label="Stopped" />
              <Swatch color="#f97316" label="Constraint" />
            </HStack>
            <Button size="xs" variant="outline" onClick={() => setShow((v) => !v)}>
              {show ? <EyeOff size={14} /> : <Eye size={14} />}
              {show ? 'Hide' : 'Show'}
            </Button>
          </HStack>
        </HStack>

        {show && (
          <Suspense
            fallback={
              <Box h="380px" display="flex" alignItems="center" justifyContent="center">
                <Text fontSize="sm" color="content.secondary">
                  Loading line visual...
                </Text>
              </Box>
            }
          >
            <PackagingLine3D
              lines={lines}
              underTestLine={TARGET_LINE}
              constraintByLine={constraintByLine}
            />
          </Suspense>
        )}
      </Card.Body>
    </Card.Root>
  )
}

function LivePackagingLine() {
  // One subscription now covers both: the tags and the platform's computed outputs share
  // the governed root, so `<root>/#` picks up everything. The tag parser and the KPI reader
  // each ignore what belongs to the other - see RESERVED_MACHINE_SEGMENTS.
  const topics = useMemo(() => `${PACKAGING_PREFIX}/#`, [])
  const { values, isConnected, messageCount } = useMqttTopicValues({
    topics,
    clientIdPrefix: 'stories_packaging_line',
  })
  const lines = useMemo(() => buildLineViews(values), [values])

  /**
   * What the platform computed, per PRD v0.3 §7.3.
   *
   * Empty until Contextualize is publishing, which is the normal state locally. The page
   * then falls back to its own derivation and labels it, rather than passing a
   * browser-side approximation off as the platform's answer.
   */
  const governed: GovernedKpiByLine = useMemo(() => buildGovernedKpi(values), [values])
  const governedConstraints = useMemo(() => {
    const now = Date.now()
    const out: Record<string, string | undefined> = {}
    for (const [line, kpi] of Object.entries(governed)) {
      if (isGovernedFresh(kpi, now) && kpi.constraintAsset !== undefined) {
        out[line] = kpi.constraintAsset
      }
    }
    return out
  }, [governed])

  // Cumulative output history per line, for the rolling rate. Counter deltas are how OEE
  // is computed and the only stable way to read a line that stops intermittently.
  const outputHistory = useRef<Record<string, OutputSample[]>>({})
  const rolling: Record<string, number | null> = {}
  for (const line of lines) {
    const output = line.machines[line.machines.length - 1]
    const units = output?.processed
    const designSpeed = output?.designSpeed
    const history = outputHistory.current[line.line] ?? []
    if (units !== null && units !== undefined && designSpeed) {
      const last = history[history.length - 1]
      if (!last || last.units !== units) {
        history.push({ at: Date.now(), units, designSpeed })
      }
      // Keep a little more than the window so the span is always sufficient.
      while (history.length > 2 && Date.now() - history[0].at > ROLLING_WINDOW_MS) {
        history.shift()
      }
      outputHistory.current[line.line] = history
    }
    rolling[line.line] = rollingLinePerformance(history)
  }

  const { hasMinRole, isLoading: authLoading } = useAuth()
  const canControl = !authLoading && hasMinRole('operator')
  const queryClient = useQueryClient()
  const [actionError, setActionError] = useState<string | null>(null)

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['scenario', 'status'],
    queryFn: () => scenarioApi.getStatus(),
    // The onset ramps over two minutes, so poll often enough to watch it move.
    refetchInterval: 5000,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['scenario', 'status'] })

  const arm = useMutation({
    mutationFn: () =>
      scenarioApi.armDeviation({
        line: TARGET_LINE,
        machine: TARGET_MACHINE,
        degradedRate: DEGRADED_RATE,
        durationMinutes: DURATION_MINUTES,
      }),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: (error) => {
      // A rejected arm is usually the cooldown guard, which is a normal answer
      // rather than a failure, so surface the server's message verbatim.
      const message =
        error instanceof ApiError
          ? ((error.data as { message?: string })?.message ?? error.statusText)
          : 'Could not arm the deviation'
      setActionError(message)
    },
  })

  const clear = useMutation({
    mutationFn: () => scenarioApi.clearDeviation(),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: () => setActionError('Could not clear the deviation'),
  })

  const armFailure = useMutation({
    mutationFn: () =>
      scenarioApi.armFailure({
        line: TARGET_LINE,
        machine: FAILURE_MACHINE,
        durationMinutes: FAILURE_DURATION_MINUTES,
      }),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: (error) => {
      const message =
        error instanceof ApiError
          ? ((error.data as { message?: string })?.message ?? error.statusText)
          : 'Could not arm the failure'
      setActionError(message)
    },
  })

  const clearFailure = useMutation({
    mutationFn: () => scenarioApi.clearFailure(),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: () => setActionError('Could not clear the failure'),
  })

  const deviation = status?.deviation
  const active = Boolean(deviation)
  const failure = status?.failure
  const failed = Boolean(failure)
  // Fall back to `baseline` only while the status is still loading; the badge above is
  // gated on statusLoading so an unknown phase is never rendered as a known one.
  const phasePresentation = PHASE_PRESENTATION[status?.phase ?? 'baseline']
  // True when the platform is publishing fresh computed outputs for the line under test.
  const governedActive = isGovernedFresh(governed[TARGET_LINE], Date.now())
  const cooldown = status?.cooldownRemainingSeconds ?? 0

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Lisbon Packaging"
        subtitle="Live performance for two packaging lines running the same SKU, with the degradation controls for presenters."
      />

      <VStack align="stretch" gap={8}>
        {/* Scenario state and presenter controls, merged into the live view so a
            presenter never has to leave the data to drive it. */}
        <Card.Root borderLeftWidth="4px" borderLeftColor={active ? 'orange.solid' : 'green.solid'}>
          <Card.Body p={5}>
            <VStack align="stretch" gap={4}>
              <HStack justify="space-between" flexWrap="wrap" gap={4}>
                <VStack align="start" gap={1}>
                  <HStack gap={2} flexWrap="wrap">
                    {/*
                      Don't claim "Healthy" before the status has loaded - an absent
                      deviation and an unknown one look identical otherwise.
                    */}
                    {statusLoading ? (
                      <Badge colorPalette="gray" variant="subtle">
                        Checking status
                      </Badge>
                    ) : (
                      <Badge colorPalette={phasePresentation.palette} variant="solid">
                        {phasePresentation.label}
                      </Badge>
                    )}
                    {/*
                      Where the KPI and the constraint came from. The demo's argument is
                      that the platform computes these, so a locally derived stand-in must
                      never be shown as if it were the platform's own answer.
                    */}
                    <Badge
                      colorPalette={governedActive ? 'purple' : 'gray'}
                      variant="subtle"
                      title={
                        governedActive
                          ? 'Line KPI and constraint read from the governed kpi/ topics.'
                          : 'The platform is not publishing computed outputs; these values are derived in the browser.'
                      }
                    >
                      {governedActive ? 'KPI: platform' : 'KPI: derived locally'}
                    </Badge>
                    <Badge colorPalette={isConnected ? 'green' : 'red'} variant="subtle">
                      <HStack gap={1}>
                        {isConnected ? <Activity size={12} /> : <WifiOff size={12} />}
                        <Text>{isConnected ? 'Live' : 'Disconnected'}</Text>
                      </HStack>
                    </Badge>
                    <Badge colorPalette="gray" variant="subtle">
                      <HStack gap={1}>
                        <Layers size={12} />
                        <Text>{messageCount} messages</Text>
                      </HStack>
                    </Badge>
                    {failed && failure && (
                      <Badge colorPalette="red" variant="solid">
                        <HStack gap={1}>
                          <ShieldAlert size={12} />
                          <Text>{MACHINE_LABELS[failure.machine] ?? failure.machine} down</Text>
                        </HStack>
                      </Badge>
                    )}
                    {status?.running === false && (
                      <Badge colorPalette="red" variant="subtle">
                        Publisher stopped
                      </Badge>
                    )}
                  </HStack>

                  {active && deviation ? (
                    <Text fontSize="sm" color="content.secondary">
                      {LINE_LABELS[deviation.line] ?? deviation.line} /{' '}
                      {MACHINE_LABELS[deviation.machine] ?? deviation.machine} held at{' '}
                      {deviation.degradedRate} units/min, {deviation.minutesRemaining} min
                      remaining, onset {deviation.progressPct}%, settle {deviation.settlePct}%
                    </Text>
                  ) : (
                    <Text fontSize="sm" color="content.secondary">
                      {phasePresentation.hint}
                    </Text>
                  )}
                  {/*
                    The claim convention. A cooldown and a status badge stop two presenters
                    colliding by accident; nothing in software stops them colliding on
                    purpose, so the page has to say where to coordinate.
                  */}
                  {failed && failure && (
                    <Text fontSize="sm" color="content.secondary">
                      {LINE_LABELS[failure.line] ?? failure.line} /{' '}
                      {MACHINE_LABELS[failure.machine] ?? failure.machine} is Held and producing
                      nothing, {failure.minutesRemaining} min remaining
                    </Text>
                  )}
                  {(active || failed) && (
                    <Text fontSize="xs" color="content.tertiary">
                      Someone is mid-demo. Claim the environment in #se-demo-env before triggering
                      over the top of a live customer call.
                    </Text>
                  )}
                </VStack>

                {canControl ? (
                  <HStack gap={3} flexWrap="wrap">
                    <Button
                      onClick={() => arm.mutate()}
                      loading={arm.isPending}
                      disabled={cooldown > 0}
                    >
                      <Play size={16} />
                      {active ? 'Extend degradation' : 'Trigger degradation'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => clear.mutate()}
                      loading={clear.isPending}
                      disabled={!active || deviation?.clearing}
                    >
                      <Square size={16} />
                      Clear
                    </Button>
                    {/*
                      No cooldown on this one: a breakdown has no onset to protect and is
                      meant to be repeatable within a session, so re-arming just replaces
                      the running one.
                    */}
                    <Button
                      colorPalette="red"
                      variant={failed ? 'outline' : 'solid'}
                      onClick={() => (failed ? clearFailure.mutate() : armFailure.mutate())}
                      loading={armFailure.isPending || clearFailure.isPending}
                    >
                      <ShieldAlert size={16} />
                      {failed ? 'Repair machine' : 'Trigger failure'}
                    </Button>
                    {cooldown > 0 && (
                      <HStack gap={1}>
                        <Clock size={14} />
                        <Text fontSize="sm" color="content.secondary">
                          Cooldown {cooldown}s
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                ) : (
                  <Text fontSize="sm" color="content.secondary" maxW="sm" textAlign="right">
                    Operator access is required to trigger a degradation. The live view is read-only
                    for everyone else.
                  </Text>
                )}
              </HStack>

              {actionError && (
                <HStack gap={2} color="orange.fg">
                  <AlertTriangle size={16} />
                  <Text fontSize="sm">{actionError}</Text>
                </HStack>
              )}

              {canControl && (
                <Text fontSize="xs" color="content.tertiary">
                  Triggering degrades {MACHINE_LABELS[TARGET_MACHINE]} on {LINE_LABELS[TARGET_LINE]}{' '}
                  to {DEGRADED_RATE} units/min for {DURATION_MINUTES} minutes; the blocked and
                  starved cascade on its neighbours follows automatically. Triggering again extends
                  rather than restarts, so a second presenter cannot reset someone else's demo, and
                  the deviation runs server-side so it survives closing this tab.
                </Text>
              )}

              {canControl && (
                <Text fontSize="xs" color="content.tertiary">
                  Triggering a failure puts {MACHINE_LABELS[FAILURE_MACHINE]} into Held at zero
                  output for {FAILURE_DURATION_MINUTES} minutes. A stopped machine and a slow one
                  are different faults, which is why this is a separate button from the degradation
                  above - naming the KPI each one moves is the platform's job, not this page's.
                  Watch{' '}
                  <Code fontSize="xs">
                    BBCorp/Lisbon/Packaging/{TARGET_LINE}/{FAILURE_MACHINE}/StateCurrent
                  </Code>{' '}
                  go to 11 and <Code fontSize="xs">ActualProductionRate</Code> go to 0.
                </Text>
              )}
            </VStack>
          </Card.Body>
        </Card.Root>

        {lines.length === 0 ? (
          <Card.Root>
            <Card.Body p={8}>
              <VStack gap={2} textAlign="center">
                <Heading size="sm">Waiting for line data</Heading>
                <Text fontSize="sm" color="content.secondary" maxW="lg">
                  No messages on <code>{PACKAGING_PREFIX}/#</code> yet. Locally, start the broker
                  and the scenario publisher; on the cluster, check the Edge adapters.
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        ) : (
          <>
            <LineVisual lines={lines} constraintByLine={governedConstraints} />
            <CrossLineComparison lines={lines} />
            {lines.map((line) => (
              <LineSection
                key={line.line}
                line={line}
                role={line.line === TARGET_LINE ? 'under test' : 'control group'}
                rolling={rolling[line.line] ?? null}
              />
            ))}
          </>
        )}

        {/*
          The gap this page deliberately leaves visible: PackML reports SUSPENDED for
          both blockage and starvation, so the raw feed cannot say which. Naming it is
          what the platform's Contextualize layer adds.
        */}
        <VStack align="stretch" gap={3}>
          <SectionHeading
            title="What the raw signals cannot tell you"
            description="PackML reports SUSPENDED for an external stop, whether the machine is blocked by what is downstream or starved by what is upstream."
          />
          <Card.Root bg="bg.subtle">
            <Card.Body p={5}>
              <VStack align="start" gap={2}>
                <Text fontSize="sm" color="content.secondary">
                  Separating the two needs the buffer levels: a suspended machine with a full
                  outfeed is blocked, and one with an empty infeed is starved. The machine actually
                  limiting the line is neither, and is still reporting Producing.
                </Text>
                <Text fontSize="sm" color="content.secondary">
                  That derivation, the governed namespace, the rolling KPI and the agent that acts
                  on them all live in the platform, not here.
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>
      </VStack>
    </Box>
  )
}
